// Copyright (c) Bentley Systems
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  checkInternalServerError,
  getParametersFromUrlString,
  getQueryParam,
  setErrorState,
} from "../Helpers/helper-functions";
import { selectAccessToken } from "../app/authorization-slice";
import { setConfirmation } from "../app/confirmation-slice";
import { Button, ProgressRadial } from "@itwin/itwinui-react";
import { ConfigManager } from "../Helpers/config-manager";
import { mapDataToModels } from "../Helpers/map-response";
import { ItwinRole } from "./Models/itwin-role";
import { EdfsRole } from "./Models/edfs-role";
import { Assignment } from "./Models/assignments";
import RoleAssignmentTable from "./RoleAssignmentTable";
import { useToastNotificationService } from "../Helpers/ToastNotificationService";
import { DawComponent } from "../Helpers/daw-component";
import { useNodePermission } from "../Helpers/useNodePermission";

/**
 * Props for the ManageAccessRolesAssignmentForm component
 *
 * This component supports two initialization modes:
 * 1. Direct props (packageUniqueName, iTwinId) - when opened from SubscribedProjectList
 * 2. URL parameters - when opened from Elist dynamic routing
 */
export type ManageAccessProps = {
  /** Direct package identifier (optional, can be extracted from parameters) */
  packageUniqueName?: string | undefined;
  /** iTwin project identifier (optional, can be extracted from parameters) */
  iTwinId?: string | undefined;
  /** URL parameter string in format "iTwinId=xxx&packageUniqueName=yyy" */
  parameters?: string | undefined;
  /** Callback to close the form and optionally refresh parent list */
  closeFrollFunction?: (refreshList: boolean) => void;
  /** Callback for nested navigation within information panel */
  navigateToInformationPanelFunction?: (navFrolUrl: string) => void;
  /** Callback to update the information panel header text */
  setInforPanelHeaderFunction?: (header: string) => void;
};

const ManageAccessRolesAssignmentForm = (props: ManageAccessProps) => {
  const dispatch = useAppDispatch();
  const iTwinQueryParam = getQueryParam("iTwinId");
  const { isNodeAllowed } = useNodePermission(
    DawComponent.DAW_COMPONENT_MANAGE_ACCESS_ROLES_ASSIGNMENT_FORM,
    {
      extraHeaders: iTwinQueryParam
        ? { "Itwin-ID": iTwinQueryParam }
        : undefined,
    },
  );
  const { showToast } = useToastNotificationService();
  const [isFormDataChanged, setIsFormDataChanged] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(true);
  const accessToken = useAppSelector(selectAccessToken);
  const [iTwinRoles, setITwinRoles] = useState<ItwinRole[]>([]);
  const [edfsRoles, setEdfsRoles] = useState<EdfsRole[]>([]);
  const [initialAssignments, setInitialAssignments] = useState<Assignment[]>(
    [],
  );
  const [currentAssignments, setCurrentAssignments] = useState<Assignment[]>(
    [],
  );
  /**
   * iTwinId can be provided via:
   * 1. URL parameters (takes precedence) from generated list actions
   * 2. Direct props (fallback) from SubscribedProjectList
   */
  const [iTwinId, setITwinId] = useState<string | undefined>(props.iTwinId);
  /**
   * packageUniqueName can be provided via:
   * 1. URL parameters (takes precedence) from generated list actions
   * 2. Direct props (fallback) from SubscribedProjectList
   */
  const [packageUniqueName, setPackageUniqueName] = useState<
    string | undefined
  >(props.packageUniqueName);

  const urlParameters = useMemo(
    () => getParametersFromUrlString(props.parameters),
    [props.parameters],
  );



  useEffect(() => {
    const iTwinIdPar = urlParameters.find((p) => p.name === "iTwinId");
    const itId = iTwinIdPar?.value;
    if (itId) {
      setITwinId(itId);
    }

    const packageUniqueNamePar = urlParameters.find(
      (p) => p.name === "packageUniqueName",
    );
    const uniqueName = packageUniqueNamePar?.value;
    if (uniqueName) {
      setPackageUniqueName(uniqueName);
      if (props.setInforPanelHeaderFunction) {
        const header = `Manage access to ${uniqueName}`;
        props.setInforPanelHeaderFunction(header);
      }
    }
  }, [urlParameters]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchItwinRoles = async (iTwinIdStr: string) => {
      const url =
        `${ConfigManager.iTwinPlatformUrl}/accesscontrol/itwins/${iTwinIdStr}/roles`;
      const runId = crypto.randomUUID();
      try {
        const rolesResp = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal: abortController.signal,
        });
        if (!rolesResp.ok) {
          setErrorState(rolesResp.status, dispatch, runId, true);
          return null;
        }
        const iTwinRolesData = await rolesResp.json();
        return mapDataToModels<ItwinRole>(iTwinRolesData.roles);
      } catch (error: any) {
        if (error.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
        return null;
      }
    };

    const fetchEdfsRoles = async () => {
      const edfsRolesUrl =
        //ConfigManager.iTwinPlatformUrl + `/edfs/packages/roles`;
        `${ConfigManager.edfsItwinInternalUrl}/packages/roles`;
      const runIdEdfsRoles = crypto.randomUUID();
      try {
        const edfsRolesResp = await fetch(edfsRolesUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runIdEdfsRoles,
          },
          signal: abortController.signal,
        });
        if (!edfsRolesResp.ok) {
          setErrorState(edfsRolesResp.status, dispatch, runIdEdfsRoles, true);
          return null;
        }
        const edfsRolesData = await edfsRolesResp.json();
        return mapDataToModels<EdfsRole>(edfsRolesData.packageRoles);
      } catch (error: any) {
        if (error.name !== "AbortError") {
          setErrorState(500, dispatch, runIdEdfsRoles, true);
        }
        return null;
      }
    };

    const fetchAssignments = async (iTwinIdStr: string, uniqueName: string) => {
      const assignUrl =
        // ConfigManager.iTwinPlatformUrl +
        // `/edfs/itwins/${iTwinId}/packages/${uniqueName}/roles`;
        `${ConfigManager.edfsItwinInternalUrl}/itwins/${iTwinIdStr}/packages/${uniqueName}/roles`;
      const runIdAssign = crypto.randomUUID();
      try {
        const assignResp = await fetch(assignUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runIdAssign,
          },
          signal: abortController.signal,
        });
        if (!assignResp.ok) {
          setErrorState(assignResp.status, dispatch, runIdAssign, true);
          return null;
        }
        const assignData = await assignResp.json();
        return mapDataToModels<Assignment>(assignData.assignments);
      } catch (error: any) {
        if (error.name !== "AbortError") {
          setErrorState(500, dispatch, runIdAssign, true);
        }
        return null;
      }
    };

    const loadRoles = async (iTwinIdStr: string, uniqueName: string) => {
      setLoading(true);
      try {
        const [iTwinRolesResult, edfsRolesResult, assignmentsResult] =
          await Promise.allSettled([
            fetchItwinRoles(iTwinIdStr),
            fetchEdfsRoles(),
            fetchAssignments(iTwinIdStr, uniqueName),
          ]);

        if (!abortController.signal.aborted) {
          // Handle each result individually
          if (
            iTwinRolesResult.status === "fulfilled" &&
            iTwinRolesResult.value
          ) {
            setITwinRoles(iTwinRolesResult.value);
          } else {
            showToast({
              status: "negative",
              message:
                "Failed to load iTwin roles. Some features may be limited.",
            });
          }

          if (edfsRolesResult.status === "fulfilled" && edfsRolesResult.value) {
            setEdfsRoles(edfsRolesResult.value);
          } else {
            showToast({
              status: "negative",
              message:
                "Failed to load package roles. Some features may be limited.",
            });
          }

          if (
            assignmentsResult.status === "fulfilled" &&
            assignmentsResult.value
          ) {
            setInitialAssignments(assignmentsResult.value);
            setCurrentAssignments(assignmentsResult.value);
            setIsFormDataChanged(false);
          } else {
            showToast({
              status: "negative",
              message: "Failed to load existing role assignments.",
            });
          }
        }
      } catch {
        if (!abortController.signal.aborted) {
          showToast({
            status: "negative",
            message: "An unexpected error occurred while loading form data.",
          });
        }
      } finally {
        setLoading(false);
      }
    };

    // Load roles if we have required values
    if (isNodeAllowed && iTwinId && packageUniqueName) {
      void loadRoles(iTwinId, packageUniqueName);
    }

    // Cleanup function
    return () => {
      abortController.abort();
    };
  }, [accessToken, dispatch, iTwinId, packageUniqueName, isNodeAllowed]);

  const onCancelClick = () => {
    if (isFormDataChanged) {
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: () => closeFRoll(false),
          caption: "Discard unsaved changes?",
          text: "Changes you made so far will not be updated",
          okText: "Discard anyway",
          cancelText: "Stay",
        }),
      );
    } else {
      closeFRoll(false);
    }
  };

  const closeFRoll = (refreshList: boolean) => {
    if (props.closeFrollFunction !== undefined) {
      props.closeFrollFunction(refreshList);
    }
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    await save();
  };

  const getSaveJson = (currAssignments: Assignment[]): string => {
    const formattedAssignments = currAssignments.map((assignment) => ({
      iTwinRoleId: assignment.iTwinRoleId,
      packageRoleIds: assignment.packageRoles.map((role) => role.packageRoleId),
    }));

    return JSON.stringify({ assignments: formattedAssignments });
  };

  const save = async () => {
    if (packageUniqueName) {
      setLoading(true);
      try {
        if (currentAssignments.length === 0 && initialAssignments.length > 0) {
          // If there are no assignments in the current state but there were in the initial state, delete all assignments
          const deleteUrl =
            `${ConfigManager.edfsItwinInternalUrl}/itwins/${iTwinId}/packages/${packageUniqueName}/roles/remove-itwin-roles`;
          const deleteRunId = crypto.randomUUID();

          const jsonResult = {
            iTwinRoleIds: initialAssignments.map(
              (assignment) => assignment.iTwinRoleId,
            ),
          };
          const deleteJsonBody = JSON.stringify(jsonResult, null, 2);

          const deleteResponse = await fetch(deleteUrl, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: accessToken,
              RunId: deleteRunId,
            },
            body: deleteJsonBody,
          });

          checkInternalServerError(
            deleteResponse.status,
            dispatch,
            deleteRunId,
          );
          const deleteData = await deleteResponse.json();

          if (deleteData.error) {
            showToast({
              status: "negative",
              message: `${deleteData.error.message} ActivityId="${deleteRunId}"`,
            });
          } else {
            showToast({ status: "positive", action: "update" });
            closeFRoll(true);
          }
        } else {
          const saveUrl =
            `${ConfigManager.edfsItwinInternalUrl}/itwins/${iTwinId}/packages/${packageUniqueName}/roles`;
          const jsonBody = getSaveJson(currentAssignments);
          const saveRunId = crypto.randomUUID();

          const saveResponse = await fetch(saveUrl, {
            method: initialAssignments.length > 0 ? "PATCH" : "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: accessToken,
              RunId: saveRunId,
            },
            body: jsonBody,
          });

          checkInternalServerError(saveResponse.status, dispatch, saveRunId);
          const saveData = await saveResponse.json();

          if (saveData.error) {
            showToast({
              status: "negative",
              message: `${saveData.error.message} ActivityId="${saveRunId}"`,
            });
          } else {
            showToast({ status: "positive", action: "update" });
            closeFRoll(true);
          }
        }
      } catch {
        showToast({
          status: "negative",
          message: `An error occurred while saving assignments.`,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAssignmentsChange = (newAssignments: Assignment[]) => {
    setCurrentAssignments(newAssignments);
  };

  return (
    <div
      style={{ height: "100%", overflowY: "hidden" }}
      className="iuiBackgroundColor"
    >
      {isLoading ? (
        <div
          className="becs-ltr-margin"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <ProgressRadial indeterminate />
        </div>
      ) : (
        <div style={{ height: "100%" }}>
          <form onSubmit={handleSubmit} style={{ height: "100%" }}>
            <div
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="becs-infopanelbody-footer-line"
                style={{
                  flex: 1,
                  overflowY: "auto",
                }}
              >
                <div className="becs-all-margin">
                  <RoleAssignmentTable
                    iTwinRoles={iTwinRoles}
                    edfsRoles={edfsRoles}
                    initialAssignments={initialAssignments}
                    isChanged={(isAssignmentChanged) => {
                      setIsFormDataChanged(isAssignmentChanged);
                    }}
                    onAssignmentsChange={handleAssignmentsChange}
                  />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div className="becs-all-margin">
                  <div className="rowWrap">
                    <Button
                      key="saveCredentialBtn"
                      as="button"
                      styleType="cta"
                      type="submit"
                      disabled={!isFormDataChanged}
                    >
                      <div>{"Save"}</div>
                    </Button>
                    <Button
                      key="cancelCredentialBtn"
                      as="button"
                      onClick={() => onCancelClick()}
                    >
                      <div>Cancel</div>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ManageAccessRolesAssignmentForm;
